/**
 * The keyboard, connected.
 *
 * `model/keymap.ts` has held the whole map — three tiers, chords, capability
 * gating, user remapping — since before there was a client, and it has been
 * wired to nothing. This is the wiring, and it is deliberately thin: every
 * decision about WHICH binding fires belongs to `KeyResolver`, and everything
 * here is about WHERE the keystroke came from.
 *
 * THE SUPPRESSION RULE IS THE WHOLE POINT OF THIS FILE. `interaction.md` calls
 * typing-in-a-field-triggers-a-shortcut "the single most common webmail keyboard
 * bug", and it is: without this, typing "e" into the search box archives the
 * message under the cursor. `KeyResolver` already has the mechanism — in the
 * `compose` context it fires only modifier chords — so the job here is to
 * recognise a text-entry target and hand it that context. `Mod+k` still works
 * from inside a field, which is what a command palette has to do.
 */
import { useEffect, useRef } from 'react'
import { KeyResolver, type Context, type KeyEvent, type Resolution } from './model/keymap.ts'

/** What a resolved binding is handed to. Returning false lets the browser have the key. */
export type CommandHandler = (id: string) => boolean | void

export interface KeyboardOptions {
  /** Where focus conceptually is when it is not in a field. */
  readonly context: Context
  /** Capabilities from `/whoami`, so a binding the role lacks is inert. */
  readonly capabilities?: ReadonlySet<string>
  readonly overrides?: Readonly<Record<string, string>>
  readonly onCommand: CommandHandler
  /** Told about a half-typed chord, so the UI can show `g …` in the corner. */
  readonly onPending?: (prefix: string | null) => void
  readonly enabled?: boolean
}

/**
 * Anything that takes typed text.
 *
 * `[data-hearth-editor]` is here for a rich-text composer that is neither an
 * input nor natively contenteditable — a component can opt in without this file
 * having to know it exists. `[role="textbox"]` catches the ARIA spelling of the
 * same thing.
 */
const TEXT_ENTRY = 'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [data-hearth-editor], [role="textbox"]'

function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  // `closest`, not a tag check: focus inside a contenteditable lands on whatever
  // inline element the caret is in, not on the editable host.
  return target.closest(TEXT_ENTRY) !== null
}

export function useKeyboard(options: KeyboardOptions): void {
  // THE HANDLER IS READ THROUGH A REF, so the listener is installed once and
  // does not churn. Re-registering on every render would drop a half-typed
  // chord each time a search result arrived, because the resolver holding the
  // pending prefix would be replaced mid-chord.
  const latest = useRef(options)
  latest.current = options

  const resolver = useRef<KeyResolver | null>(null)
  const signature = `${options.capabilities ? [...options.capabilities].sort().join(',') : ''}|${JSON.stringify(options.overrides ?? {})}`

  useEffect(() => {
    resolver.current = new KeyResolver({
      ...(latest.current.capabilities ? { capabilities: latest.current.capabilities } : {}),
      ...(latest.current.overrides ? { overrides: latest.current.overrides } : {}),
    })
    // Rebuilt only when the MAP changes — a new capability set or a remap —
    // never on an ordinary render.
  }, [signature])

  useEffect(() => {
    if (options.enabled === false) {
      // THE PENDING PREFIX GOES WITH THE LISTENER. Without this, a `g` pressed
      // a moment before a dialog opens stays half-typed inside the resolver,
      // and the first key after the dialog closes completes a chord the reader
      // abandoned — `g` then, much later, `i`, jumping them to the Inbox.
      resolver.current?.reset()
      latest.current.onPending?.(null)
      return
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      const current = latest.current
      const engine = resolver.current
      if (!engine) return

      // A modifier held on its own is not a keystroke. Without this, holding
      // Shift to type `!` resolves `Shift` first and clears a pending chord.
      if (event.key === 'Shift' || event.key === 'Control' || event.key === 'Alt' || event.key === 'Meta') return

      // ESCAPE IS NOT IN THE MAP, and deliberately: `interaction.md` gives it a
      // LADDER — close overlay, blur the field, dismiss the banner, clear the
      // selection, return focus to the list — which is a sequence of states
      // rather than one command. It is dispatched as `escape` and the shell
      // decides which rung applies.
      if (event.key === 'Escape') {
        // From inside a field it must blur rather than reaching the ladder,
        // which is the rung the browser would otherwise skip.
        if (isTextEntry(event.target) && event.target instanceof HTMLElement) {
          event.target.blur()
          event.preventDefault()
          return
        }
        engine.reset()
        current.onPending?.(null)
        if (current.onCommand('escape') !== false) event.preventDefault()
        return
      }

      const context: Context = isTextEntry(event.target) ? 'compose' : current.context
      const key: KeyEvent = {
        key: event.key,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        shiftKey: event.shiftKey,
        altKey: event.altKey,
      }

      const resolution: Resolution = engine.resolve(key, context)
      current.onPending?.(engine.pendingPrefix)

      if (resolution.kind === 'pending') {
        // A leader must not reach the page: `g` in a list is a prefix, and
        // letting it through would scroll or type.
        event.preventDefault()
        return
      }
      if (resolution.kind === 'none') return
      if (resolution.kind === 'blocked') {
        // The role does not hold the capability. Reported rather than silent —
        // `whoami` distinguishes "you do not have this" from "not over the
        // public path", and a key that does nothing with no explanation is the
        // worst of the three.
        if (current.onCommand(`blocked:${resolution.binding.id}`) !== false) event.preventDefault()
        return
      }

      if (current.onCommand(resolution.binding.id) !== false) event.preventDefault()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [options.enabled])
}
